import React from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import {
  getNutritionTrafficLightColor,
  getNutritionTrafficLightStatus,
} from '@/utils/nutrition-traffic-light';
import type { MealPlanClinicalBiomarkerOverlay, MealPlanClinicalInsightItem } from '@/types/meal-plan';

type MacroRow = {
  label: string;
  total: number;
  target: number;
  color: string;
  clinicalLimitLabel?: string;
};

type MealBreakdownRow = {
  label: string;
  value: string;
};

type SidebarClinicalInsights = {
  items?: MealPlanClinicalInsightItem[];
  matchedConditions?: string[];
  biomarkerOverlays?: MealPlanClinicalBiomarkerOverlay[];
  contextLabels?: string[];
  completion?: {
    filled: number;
    total: number;
  };
  isReady?: boolean;
  hasBackendPayload?: boolean;
  isLoading?: boolean;
  error?: string;
};

type NutritionalAnalysisSidebarProps = {
  totalIntake: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  macroTargets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  calorieProgress: number;
  macroRows: MacroRow[];
  mealBreakdown: MealBreakdownRow[];
  onAnalyseClick?: () => void;
  showAnalyseButton?: boolean;
  showTargets?: boolean;
  clinicalInsights?: SidebarClinicalInsights | null;
};

const formatNumber = (value: number): string =>
  !Number.isFinite(value) ? '0' : roundNutritionDisplay(Math.max(0, value));

const formatCalories = (value: number): string =>
  !Number.isFinite(value) ? '0' : roundNutritionDisplay(Math.max(0, value));

const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const clinicalInsightMeta = (severity: MealPlanClinicalInsightItem['severity']) => {
  if (severity === 'positive') {
    return {
      Icon: CheckCircleRoundedIcon,
      color: '#027a48',
      bgcolor: '#f0fdf4',
      borderColor: '#abefc6',
    };
  }
  if (severity === 'info') {
    return {
      Icon: InfoOutlinedIcon,
      color: '#175cd3',
      bgcolor: '#eff8ff',
      borderColor: '#b2ddff',
    };
  }
  return {
    Icon: WarningAmberRoundedIcon,
    color: '#b42318',
    bgcolor: '#fff5f4',
    borderColor: '#fecdca',
  };
};

const formatClinicalContext = (labels: string[]): string => {
  if (labels.length === 0) return 'client medical and lab context';
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 3).join(', ')}, +${labels.length - 3} more`;
};

const NutritionalAnalysisSidebar: React.FC<NutritionalAnalysisSidebarProps> = ({
  totalIntake,
  macroTargets,
  calorieProgress,
  macroRows,
  mealBreakdown,
  onAnalyseClick,
  showAnalyseButton = true,
  showTargets = true,
  clinicalInsights = null,
}) => {
  const hasCalorieTarget = showTargets && macroTargets.calories > 0;
  const calorieTraffic = hasCalorieTarget
    ? getNutritionTrafficLightStatus(calorieProgress)
    : null;
  const calorieDisplayColor = calorieTraffic?.color ?? '#20AEDC';
  const calorieBarColor = hasCalorieTarget
    ? getNutritionTrafficLightColor(calorieProgress)
    : '#f59e0b';
  const clinicalInsightItems = (clinicalInsights?.items ?? [])
    .filter((item) => Boolean(item?.title || item?.message));
  const clinicalContextLabels = Array.from(new Set([
    ...(clinicalInsights?.contextLabels ?? []),
    ...(clinicalInsights?.matchedConditions ?? []),
    ...(clinicalInsights?.biomarkerOverlays ?? []).map((overlay) => `${overlay.biomarker} biomarker`),
  ]
    .map((label) => String(label ?? '').trim())
    .filter(Boolean)));
  const hasClinicalContext =
    clinicalContextLabels.length > 0;
  const shouldRenderClinicalInsights = Boolean(clinicalInsights);
  const isClinicalReady = clinicalInsights?.isReady !== false;
  const completion = clinicalInsights?.completion;
  const remainingMealSlots = completion
    ? Math.max(0, completion.total - completion.filled)
    : 0;
  const completionText = completion && completion.total > 0
    ? `${completion.filled}/${completion.total} meal slots have food. `
    : '';
  const remainingText = remainingMealSlots > 0
    ? (remainingMealSlots === 1 ? '1 more slot' : `${remainingMealSlots} more slots`)
    : 'each meal slot';

  return (
  <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #dce6ec', p: 1.2, alignSelf: 'stretch', height: '100%' }}>
    <Typography sx={{ textAlign: 'center', mt: 0.9, fontFamily: 'Inter, sans-serif', fontSize: '3rem', lineHeight: 1, fontWeight: 700, color: calorieDisplayColor }}>
      {formatCalories(totalIntake.calories)}
      <Box component="span" sx={{ ml: 0.45, fontSize: '1rem', fontWeight: 500, color: '#98a2b3' }}>kcal</Box>
    </Typography>
    {showTargets ? (
      <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, sans-serif', mt: 0.35, fontSize: '0.82rem', color: '#98a2b3' }}>
        Target: {formatCalories(macroTargets.calories)} kcal
      </Typography>
    ) : null}

    {showTargets ? (
      <>
        <Box sx={{ mt: 1.2, height: 7, borderRadius: 99, bgcolor: '#edf1f5', overflow: 'hidden' }}>
          <Box sx={{ width: `${clampPercent(calorieProgress)}%`, height: '100%', bgcolor: calorieBarColor }} />
        </Box>
        <Typography sx={{ textAlign: 'center', mt: 0.45, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: calorieTraffic?.color ?? '#98a2b3' }}>
          {roundNutritionDisplay(calorieProgress)}% of daily target
          {calorieTraffic ? ` · ${calorieTraffic.label}` : ''}
        </Typography>
      </>
    ) : null}

    <Stack spacing={1} sx={{ mt: showTargets ? 1.4 : 1.8 }}>
      {macroRows.map((row) => {
        const percentOfTarget = row.target > 0 ? (row.total / row.target) * 100 : 0;
        // Same Suggested RDA traffic-light as calorie bar / nutrient report (not brand category colors).
        const barColor =
          showTargets && row.target > 0
            ? row.color
            : '#98a2b3';
        return (
          <Box key={row.label}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#667085' }}>{row.label}</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#344054' }}>
                {showTargets ? `${formatNumber(row.total)}g / ${formatNumber(row.target)}` : `${formatNumber(row.total)}g`}
              </Typography>
            </Stack>
            {showTargets && row.clinicalLimitLabel && <Typography sx={{ fontSize: 11 }}>Planning target: {formatNumber(row.target)}g · Clinical limit: {row.clinicalLimitLabel}</Typography>}
            {showTargets ? (
              <Box sx={{ mt: 0.2, height: 8, borderRadius: 99, bgcolor: '#edf1f5', overflow: 'hidden' }}>
                <Box sx={{ width: `${clampPercent(percentOfTarget)}%`, height: '100%', bgcolor: barColor }} />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Stack>
    <Box sx={{ mt: 1.6, mb: 1.6, borderTop: '1px solid #e7edf3' }} />

    <Typography sx={{ mt: 0, mb: 1, fontFamily: 'Inter, sans-serif', fontSize: '1.06rem', fontWeight: 700, color: '#1f2937' }}>
      Meal Breakdown
    </Typography>
    <Stack spacing={1.05} sx={{ minWidth: 0 }}>
      {mealBreakdown.map((row) => (
        <Box
          key={row.label}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, minWidth: 0 }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#667085', minWidth: 0, pr: 0.5 }}>
            {row.label}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              color: '#667085',
              flexShrink: 0,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Stack>

    {totalIntake.calories < 1200 ? (
      <Paper elevation={0} sx={{ mt: 1.6, borderRadius: '10px', bgcolor: '#fff8de', border: '1px solid #f2e9be', p: 0.8 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#7a5f00' }}>
          Daily intake below 1200 kcal - insufficient for this client&apos;s metabolic needs. Consider increasing portion sizes.
        </Typography>
      </Paper>
    ) : null}

    {showAnalyseButton && onAnalyseClick ? (
      <Button
        fullWidth
        variant="contained"
        onClick={onAnalyseClick}
        sx={{
          mt: 1.4,
          borderRadius: '10px',
          minHeight: 40,
          textTransform: 'none',
          bgcolor: '#20AEDC',
          '&:hover': { bgcolor: '#20AEDC' },
          fontSize: '0.9rem',
        }}
      >
        Analyse
      </Button>
    ) : null}

    {shouldRenderClinicalInsights ? (
      <Box sx={{ mt: 1.2, border: '1px solid #d9e4ec', borderRadius: '8px', bgcolor: '#fbfdff', p: 0.9 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#1f2937' }}>
          Clinical insights
        </Typography>

        {clinicalInsights?.isLoading ? (
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.8 }}>
            <CircularProgress size={14} thickness={5} sx={{ color: '#20AEDC' }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#667085' }}>
              Checking clinical targets
            </Typography>
          </Stack>
        ) : clinicalInsights?.error ? (
          <Typography sx={{ mt: 0.7, fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#667085' }}>
            Clinical insights unavailable right now.
          </Typography>
        ) : !isClinicalReady ? (
          <Box sx={{ mt: 0.8, display: 'flex', gap: 0.75, borderRadius: '8px', border: '1px solid #b2ddff', bgcolor: '#eff8ff', p: 0.75, minWidth: 0 }}>
            <InfoOutlinedIcon sx={{ mt: 0.1, fontSize: 16, color: '#175cd3', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', fontWeight: 700, color: '#175cd3', overflowWrap: 'anywhere' }}>
                Clinical checks queued
              </Typography>
              <Typography sx={{ mt: 0.25, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.35, color: '#475467', overflowWrap: 'anywhere' }}>
                {completionText}
                Add food to {remainingText} to run meal-level checks for {formatClinicalContext(clinicalContextLabels)}.
              </Typography>
            </Box>
          </Box>
        ) : clinicalInsightItems.length > 0 ? (
          <Stack spacing={0.7} sx={{ mt: 0.8 }}>
            {clinicalInsightItems.map((item, index) => {
              const meta = clinicalInsightMeta(item.severity);
              const Icon = meta.Icon;
              const key = item.id || `${item.nutrientId || item.title || 'clinical'}-${index}`;
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    gap: 0.75,
                    borderRadius: '8px',
                    border: `1px solid ${meta.borderColor}`,
                    bgcolor: meta.bgcolor,
                    p: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Icon sx={{ mt: 0.1, fontSize: 16, color: meta.color, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', fontWeight: 700, color: meta.color, overflowWrap: 'anywhere' }}>
                      {item.title || item.displayName || 'Clinical insight'}
                    </Typography>
                    {item.message ? (
                      <Typography sx={{ mt: 0.25, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.35, color: '#475467', overflowWrap: 'anywhere' }}>
                        {item.message}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ mt: 0.8, display: 'flex', gap: 0.75, borderRadius: '8px', border: '1px solid #b2ddff', bgcolor: '#eff8ff', p: 0.75, minWidth: 0 }}>
            <InfoOutlinedIcon sx={{ mt: 0.1, fontSize: 16, color: '#175cd3', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', fontWeight: 700, color: '#175cd3', overflowWrap: 'anywhere' }}>
                Clinical targets active
              </Typography>
              <Typography sx={{ mt: 0.25, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.35, color: '#475467', overflowWrap: 'anywhere' }}>
                {hasClinicalContext
                  ? `Review this day against ${formatClinicalContext(clinicalContextLabels)}. Prioritise carbohydrate quality, fibre, free sugars, sodium, saturated fat, and active micronutrients.`
                  : 'No clinical nutrient warnings for this day.'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    ) : null}
  </Paper>
  );
};

export default NutritionalAnalysisSidebar;
